/**
 * Application Routes Configuration
 * 
 * This module defines all application routes using React Router.
 * It follows enterprise standards with:
 * - Lazy loading for code splitting
 * - Protected routes for authenticated pages
 * - Proper route organization
 * - Error boundaries
 * - Integration with AuthContext
 */

import React, { Suspense, lazy } from 'react';
import { Navigate, useNavigate, useRoutes } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { logout } from '../utils/auth';

// Lazy load components for better performance
const Login = lazy(() => import('../pages/Login').then(module => ({ default: module.default })));
const Register = lazy(() => import('../pages/Register').then(module => ({ default: module.default })));
const Dashboard = lazy(() => import('../pages/Dashboard').then(module => ({ default: module.default })));
const VerifyEmail = lazy(() => import('../pages/VerifyEmail').then(module => ({ default: module.default })));
const ComingSoon = lazy(() => import('../pages/ComingSoon').then(module => ({ default: module.default })));

// Lazy load role-specific dashboard components
const StudentDashboard = lazy(() => import('../student/pages/Dashboard').then(module => ({ default: module.default })));
const ParentDashboard = lazy(() => import('../parent/pages/Dashboard').then(module => ({ default: module.default })));
const AdminDashboard = lazy(() => import('../admin/pages/Dashboard').then(module => ({ default: module.default })));
const TeacherDashboard = lazy(() => import('../teacher/pages/Dashboard').then(module => ({ default: module.default })));
const OwnerDashboard = lazy(() => import('../owner/pages/Dashboard').then(module => ({ default: module.default })));

// Loading component for lazy loaded routes
const LoadingFallback = () => (
  <div className="flex items-center justify-center min-h-screen bg-[#A5F3FC]">
    <div className="flex flex-col items-center gap-4">
      <div className="w-12 h-12 border-4 border-purple-500 border-t-transparent rounded-full animate-spin"></div>
      <p className="text-gray-600 font-medium">Loading...</p>
    </div>
  </div>
);

// Error boundary fallback component
const ErrorFallback = ({ error }) => (
  <div className="flex items-center justify-center min-h-screen bg-[#A5F3FC]">
    <div className="bg-white p-8 rounded-2xl shadow-lg max-w-md text-center">
      <h2 className="text-2xl font-bold text-red-600 mb-4">Something went wrong</h2>
      <p className="text-gray-600 mb-4">{error?.message || 'An unexpected error occurred'}</p>
      <button
        onClick={() => window.location.reload()}
        className="bg-purple-600 text-white px-6 py-2 rounded-lg hover:bg-purple-700 transition-colors"
      >
        Reload Page
      </button>
    </div>
  </div>
);

/**
 * Protected Route Component
 * 
 * Wraps routes that require authentication.
 * Uses AuthContext to check authentication status.
 * Redirects to login if not authenticated.
 */
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, isLoading } = useAuth();

  // Show loading state while checking authentication
  if (isLoading) {
    return <LoadingFallback />;
  }

  // Redirect to login if not authenticated
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // Render children if authenticated
  return children;
};

/**
 * Public Route Component
 * 
 * Wraps routes that should only be accessible when not authenticated.
 * Redirects to dashboard if already authenticated.
 */
const PublicRoute = ({ children }) => {
  const { isAuthenticated, isLoading } = useAuth();

  // Show loading state while checking authentication
  if (isLoading) {
    return <LoadingFallback />;
  }

  // Redirect to dashboard if already authenticated
  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />;
  }

  // Render children if not authenticated
  return children;
};

/**
 * Dashboard wrapper component with logout functionality
 * This component provides handleLogout function to Dashboard
 */
const DashboardWithLogout = () => {
  const navigate = useNavigate();
  const { logout: authLogout } = useAuth();

  const handleLogout = async () => {
    try {
      // Clear authentication data using AuthContext
      authLogout();
      
      // Navigate to login page
      navigate('/login', { replace: true });
    } catch (error) {
      console.error('Logout error:', error);
      // Even if there's an error, clear auth and navigate to login
      authLogout();
      navigate('/login', { replace: true });
    }
  };

  return <Dashboard handleLogout={handleLogout} />;
};

/**
 * Role-specific dashboard wrapper components
 * Each provides handleLogout function to its respective dashboard
 */
const StudentDashboardWithLogout = () => {
  const navigate = useNavigate();
  const { logout: authLogout } = useAuth();

  const handleLogout = async () => {
    try {
      authLogout();
      navigate('/login', { replace: true });
    } catch (error) {
      console.error('Logout error:', error);
      authLogout();
      navigate('/login', { replace: true });
    }
  };

  return <StudentDashboard handleLogout={handleLogout} />;
};

const ParentDashboardWithLogout = () => {
  const navigate = useNavigate();
  const { logout: authLogout } = useAuth();

  const handleLogout = async () => {
    try {
      authLogout();
      navigate('/login', { replace: true });
    } catch (error) {
      console.error('Logout error:', error);
      authLogout();
      navigate('/login', { replace: true });
    }
  };

  return <ParentDashboard handleLogout={handleLogout} />;
};

const AdminDashboardWithLogout = () => {
  const navigate = useNavigate();
  const { logout: authLogout } = useAuth();

  const handleLogout = async () => {
    try {
      authLogout();
      navigate('/login', { replace: true });
    } catch (error) {
      console.error('Logout error:', error);
      authLogout();
      navigate('/login', { replace: true });
    }
  };

  return <AdminDashboard handleLogout={handleLogout} />;
};

const TeacherDashboardWithLogout = () => {
  const navigate = useNavigate();
  const { logout: authLogout } = useAuth();

  const handleLogout = async () => {
    try {
      authLogout();
      navigate('/login', { replace: true });
    } catch (error) {
      console.error('Logout error:', error);
      authLogout();
      navigate('/login', { replace: true });
    }
  };

  return <TeacherDashboard handleLogout={handleLogout} />;
};

const OwnerDashboardWithLogout = () => {
  const navigate = useNavigate();
  const { logout: authLogout } = useAuth();

  const handleLogout = async () => {
    try {
      authLogout();
      navigate('/login', { replace: true });
    } catch (error) {
      console.error('Logout error:', error);
      authLogout();
      navigate('/login', { replace: true });
    }
  };

  return <OwnerDashboard handleLogout={handleLogout} />;
};

/**
 * Register wrapper component with navigation to login
 */
const RegisterWithLogin = () => {
  const navigate = useNavigate();

  const handleLoginClick = () => {
    navigate('/login');
  };

  return <Register onLoginClick={handleLoginClick} />;
};

/**
 * Login wrapper component with navigation to register
 */
const LoginWithRegister = () => {
  const navigate = useNavigate();

  const handleRegisterClick = () => {
    navigate('/register');
  };

  return <Login onRegisterClick={handleRegisterClick} />;
};

/**
 * Route definitions
 * Each route follows pattern:
 * - path: URL path
 * - element: Component to render
 * - children: Nested routes (if any)
 */
const routes = [
  {
    path: '/',
    element: <Navigate to="/dashboard" replace />,
  },
  {
    path: '/login',
    element: (
      <PublicRoute>
        <Suspense fallback={<LoadingFallback />}>
          <LoginWithRegister />
        </Suspense>
      </PublicRoute>
    ),
  },
  {
    path: '/register',
    element: (
      <PublicRoute>
        <Suspense fallback={<LoadingFallback />}>
          <RegisterWithLogin />
        </Suspense>
      </PublicRoute>
    ),
  },
  {
    path: '/verify-email',
    element: (
      <Suspense fallback={<LoadingFallback />}>
        <VerifyEmail />
      </Suspense>
    ),
  },
  {
    path: '/dashboard',
    element: (
      <ProtectedRoute>
        <Suspense fallback={<LoadingFallback />}>
          <DashboardWithLogout />
        </Suspense>
      </ProtectedRoute>
    ),
  },
  {
    path: '/dashboard/student',
    element: (
      <ProtectedRoute>
        <Suspense fallback={<LoadingFallback />}>
          <StudentDashboardWithLogout />
        </Suspense>
      </ProtectedRoute>
    ),
  },
  {
    path: '/dashboard/parent',
    element: (
      <ProtectedRoute>
        <Suspense fallback={<LoadingFallback />}>
          <ParentDashboardWithLogout />
        </Suspense>
      </ProtectedRoute>
    ),
  },
  {
    path: '/dashboard/admin',
    element: (
      <ProtectedRoute>
        <Suspense fallback={<LoadingFallback />}>
          <AdminDashboardWithLogout />
        </Suspense>
      </ProtectedRoute>
    ),
  },
  {
    path: '/dashboard/teacher',
    element: (
      <ProtectedRoute>
        <Suspense fallback={<LoadingFallback />}>
          <TeacherDashboardWithLogout />
        </Suspense>
      </ProtectedRoute>
    ),
  },
  {
    path: '/dashboard/owner',
    element: (
      <ProtectedRoute>
        <Suspense fallback={<LoadingFallback />}>
          <OwnerDashboardWithLogout />
        </Suspense>
      </ProtectedRoute>
    ),
  },
  {
    path: '*',
    element: <Navigate to="/dashboard" replace />,
  },
];

/**
 * Main Routes Component
 * This component uses useRoutes hook to render route configuration
 * 
 * @returns {JSX.Element} The matched route element
 */
const AppRoutes = () => {
  const element = useRoutes(routes);
  
  return element;
};

export default AppRoutes;
